import { supabase } from '@/shared/config/supabase';
import { IWorkspaceSettingsRepository } from '../../domain/repositories/IWorkspaceSettingsRepository';
import { WorkspaceSettingsDTO, DEFAULT_FEATURES } from '../../application/dto/WorkspaceSettingsDTO';

/**
 * Supabase implementation of workspace settings repository
 */
export class SupabaseWorkspaceSettingsRepository implements IWorkspaceSettingsRepository {
  /**
   * Get workspace settings by workspace ID
   */
  async getSettings(workspaceId: number): Promise<WorkspaceSettingsDTO | null> {
    const { data, error } = await supabase
      .from('Workspaces')
      .select('id, settings')
      .eq('id', workspaceId)
      .single();

    if (error) {
      console.error('Error fetching workspace settings:', error);
      return null;
    }

    if (!data) {
      return null;
    }

    // Parse settings and merge with defaults
    const settings = (data.settings as Record<string, unknown>) || {};
    const features = (settings.features as Record<string, boolean>) || {};

    return {
      workspaceId: data.id,
      features: {
        lmsEnabled: features.lmsEnabled ?? DEFAULT_FEATURES.lmsEnabled,
        notificationsEnabled: features.notificationsEnabled ?? DEFAULT_FEATURES.notificationsEnabled,
        statisticsEnabled: features.statisticsEnabled ?? DEFAULT_FEATURES.statisticsEnabled,
        allowLessonDownloads: features.allowLessonDownloads ?? DEFAULT_FEATURES.allowLessonDownloads,
      },
    };
  }

  /**
   * Update a specific feature flag in workspace settings
   */
  async updateFeature(workspaceId: number, feature: string, value: boolean): Promise<void> {
    console.log('[WorkspaceSettings] Updating feature:', { workspaceId, feature, value });

    // First, get current settings to merge
    const { data: currentData, error: fetchError } = await supabase
      .from('Workspaces')
      .select('settings')
      .eq('id', workspaceId)
      .single();

    if (fetchError) {
      console.error('[WorkspaceSettings] Error fetching current settings:', fetchError);
      throw new Error(`Error al obtener configuración actual: ${fetchError.message}`);
    }

    console.log('[WorkspaceSettings] Current settings:', currentData?.settings);

    // Merge existing settings with new feature value
    const currentSettings = (currentData?.settings as Record<string, unknown>) || {};
    const currentFeatures = (currentSettings.features as Record<string, boolean>) || {};

    const updatedSettings = {
      ...currentSettings,
      features: {
        ...currentFeatures,
        [feature]: value,
      },
    };

    console.log('[WorkspaceSettings] Updating to:', updatedSettings);

    // Use RPC (POST) instead of .update() (PATCH) to avoid CORS issues
    const { data: updateData, error: updateError } = await supabase
      .rpc('update_workspace_settings', {
        p_workspace_id: workspaceId,
        p_settings: updatedSettings,
      });

    if (updateError) {
      console.error('[WorkspaceSettings] Error updating workspace settings:', updateError);
      if (updateError.code === '42501' || updateError.message?.includes('permisos')) {
        throw new Error('No tienes permisos para actualizar la configuración.');
      }
      throw new Error(`Error al actualizar configuración: ${updateError.message}`);
    }

    console.log('[WorkspaceSettings] Update successful, new settings:', updateData);
  }
}
